export interface Organization {
  id: string;
  name: string;
  slug: string;
  userCount: number;
  propertyCount: number;
  deviceCount: number;
  createdAt: string;
}

export interface Invite {
  id: string;
  organizationId: string;
  email: string;
  role: UserRole;
  isAccepted: boolean;
  token: string;
  expiresAt: string;
  createdAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
}

export enum UserRole {
  Owner = 'Owner',
  Admin = 'Admin',
  Manager = 'Manager',
  Viewer = 'Viewer',
}

export interface UserMembership {
  organizationId: string;
  organizationName: string;
  role: UserRole;
}

export interface User {
  id: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatarUrl?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  memberships?: UserMembership[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: User;
}

export interface RegisterRequest {
  organizationId: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface UserOrgMembership {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  role: UserRole;
  joinedAt: string;
}

// User management types (admin-only)
export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  isActive?: boolean;
}

export interface ResetPasswordRequest {
  newPassword: string;
}

// Property types
export enum PropertyType {
  Apartment = 'Apartment',
  Condo = 'Condo',
  StripMall = 'StripMall',
  Commercial = 'Commercial',
}

export interface Property {
  id: string;
  organizationId: string;
  name: string;
  address: string;
  propertyType: PropertyType;
  latitude?: number;
  longitude?: number;
  buildingCount: number;
  unitCount: number;
  deviceCount: number;
  activeAlarmCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePropertyRequest {
  name: string;
  address: string;
  propertyType: PropertyType;
  latitude?: number;
  longitude?: number;
}

export interface AssignGatewayRequest {
  gatewayEui: string;
}

export interface UpdatePropertyRequest {
  name?: string;
  address?: string;
  propertyType?: PropertyType;
  // When true, the backend nulls out Latitude/Longitude (and Address where
  // applicable) regardless of any latitude/longitude values supplied. The
  // existing partial-update pattern can't distinguish "not provided" from
  // "explicitly null" since fields default to undefined.
  clearLocation?: boolean;
}

// Building types
export interface Building {
  id: string;
  propertyId: string;
  propertyName: string;
  name: string;
  unitCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBuildingRequest {
  propertyId: string;
  name: string;
}

export interface UpdateBuildingRequest {
  name?: string;
}

// Unit types
export enum UnitStatus {
  Occupied = 'Occupied',
  Vacant = 'Vacant',
}

export interface Unit {
  id: string;
  buildingId: string;
  buildingName: string;
  propertyId: string;
  propertyName: string;
  unitNumber: string;
  floorNumber?: number;
  area?: number;
  tenantName?: string;
  tenantContact?: string;
  status: UnitStatus;
  deviceDevEuis: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUnitRequest {
  buildingId: string;
  unitNumber: string;
  floorNumber?: number;
  area?: number;
  tenantName?: string;
  tenantContact?: string;
  status?: UnitStatus;
}

export interface UpdateUnitRequest {
  unitNumber?: string;
  floorNumber?: number;
  area?: number;
  tenantName?: string;
  tenantContact?: string;
  status?: UnitStatus;
}

// Device types
export enum DeviceType {
  WaterMeter = 'WaterMeter',
  SALTTransmitter = 'SALTTransmitter',
}

export enum DeviceStatus {
  Provisioned = 'Provisioned',
  Active = 'Active',
  Inactive = 'Inactive',
  Error = 'Error',
}

export interface DeviceAlertSummary {
  alertType: string;
  displayLabel: string;
  detectedAt: string;
}

// May 15 batch — what meter the SALT transmitter is wired to.
// Matches the Norgas.Domain.Enums.ConnectedMeterType code enum. `Other`
// is paired with `connectedMeterTypeCustom` on Device for a free-text
// label.
export enum ConnectedMeterType {
  Gas = 'Gas',
  Electric = 'Electric',
  Water = 'Water',
  Runtime = 'Runtime',
  Other = 'Other',
}

export interface Device {
  id: string;
  unitId?: string;
  unitNumber?: string;
  buildingId?: string;
  buildingName?: string;
  propertyId?: string;
  propertyName?: string;
  organizationId: string;
  devEui: string;
  serialNumber: string;
  deviceType: DeviceType;
  model?: string;
  firmwareVersion?: string;
  status: DeviceStatus;
  lastSeenAt?: string;
  joinedAt?: string;
  latitude?: number;
  longitude?: number;
  unitAssignedAt?: string;
  latestTransmission?: MeterReading;
  activeAlerts: DeviceAlertSummary[];
  externalMeterSerial?: string;
  // Make/model of the upstream mechanical meter the SALT is wired to
  // (e.g. "Versamag"). Free-form, SALT-only.
  externalMeterModel?: string;
  // Reading on the meter's physical readout at install. Added to the
  // multiplier-applied totalization so the displayed total matches the dial.
  mechanicalOffset?: number;
  // Auto-captured raw cumulative pulse count from the device's first
  // post-assignment transmission. Subtracted at display time so we count
  // usage since install rather than the device's lifetime cumulative.
  // Surfaced for diagnostics — installers don't edit this directly.
  baselineTotalization?: number;
  // Active Pulse SALT calibration. Numeric DeviceMultiplier /
  // DeviceUnitOfMeasure values (e.g. 10 for 1:10, 0 for Gallons).
  // Both undefined when no pulse config has been saved.
  pulseMultiplier?: number;
  pulseUnitOfMeasure?: number;
  // May 15 batch — SALT-only fields.
  // What the SALT transmitter is wired to. Null on uMeter and on SALT
  // devices the installer hasn't set yet.
  connectedMeterType?: ConnectedMeterType;
  // Free-text label paired with connectedMeterType when value is Other.
  connectedMeterTypeCustom?: string;
  // Per-device pulse-multiplier override. Stored as the divisor N;
  // render as "1:N". Wins over the enum at display time.
  customPulseMultiplier?: number;
  // Per-device unit-of-measure override. Wins over the SALT lookup at
  // display time. Free-text — no validation against the global table.
  customPulseUnitOfMeasure?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeviceRequest {
  unitId?: string;
  devEui: string;
  serialNumber?: string;
  deviceType: DeviceType;
  model?: string;
  firmwareVersion?: string;
  externalMeterSerial?: string;
  externalMeterModel?: string;
  mechanicalOffset?: number;
}

export interface UpdateDeviceRequest {
  unitId?: string;
  model?: string;
  firmwareVersion?: string;
  status?: DeviceStatus;
  latitude?: number;
  longitude?: number;
  notes?: string;
  externalMeterSerial?: string;
  externalMeterModel?: string;
  mechanicalOffset?: number;
  // When true, the backend nulls out latitude/longitude regardless of the
  // values supplied. The partial-update pattern uses HasValue gating, so
  // sending `latitude: null` from the client is indistinguishable from
  // omitting it; this flag is the explicit "unset" signal.
  clearLocation?: boolean;
  // May 15 batch — SALT additions.
  connectedMeterType?: ConnectedMeterType;
  connectedMeterTypeCustom?: string;
  customPulseMultiplier?: number;
  customPulseUnitOfMeasure?: string;
}

// Provisioned meter types
export interface ProvisionedMeter {
  id: string;
  serialNumber: string;
  devEui: string;
  joinEui: string;
  appKey: string;
  provisionedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProvisionedMeterRequest {
  serialNumber: string;
  devEui: string;
  joinEui: string;
  appKey: string;
}

export interface UpdateProvisionedMeterRequest {
  serialNumber?: string;
  devEui?: string;
  joinEui?: string;
  appKey?: string;
}

// Meter reading types
export interface MeterReading {
  id: number;
  messageId: string;
  gatewayEui?: string;
  rawPayload?: string;
  unitId?: string;
  unitNumber?: string;
  timestamp: string;
  // Raw value persisted by the decoder lambda — audit-of-truth for billing
  // reconciliation. Use `computedWaterTotalization` for display: it has the
  // active SALT multiplier applied (or equals the raw value for uMeter).
  waterTotalization: number;
  computedWaterTotalization: number;
  temperatureF?: number;
  unitOfMeasure: string;
  // Effective unit of measure after applying any active SALT reconfiguration.
  // Falls back to `unitOfMeasure` when no config history applies.
  computedUnitOfMeasure: string;
  pipeEmpty: boolean;
  noUsage30Days: boolean;
  minTemperatureExceeded: boolean;
  maxTemperatureExceeded: boolean;
  lowSignal: boolean;
  burstPipeDetected: boolean;
  leakDetected: boolean;
  reverseFlowDetected: boolean;
  lowBattery: boolean;
  lowTemperature: boolean;
  sensusReadError: boolean;
  coverRemoved: boolean;
  rssi?: number;
  snr?: number;
  spreadingFactor?: number;
  bandwidth?: number;
  frequency?: string;
}

// Pagination types
export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Gateway types
export enum GatewayStatus {
  Provisioning = 'Provisioning',
  Active = 'Active',
  Inactive = 'Inactive',
  Error = 'Error',
}

export interface Gateway {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  gatewayEui: string;
  rfRegion: string;
  status: GatewayStatus;
  awsWirelessGatewayId?: string;
  certificateId?: string;
  certificateGenerated: boolean;
  lastSeenAt?: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GatewayDevice {
  id: string;
  devEui: string;
  serialNumber: string;
  deviceType: DeviceType;
  status: DeviceStatus;
  propertyId?: string | null;
  propertyName?: string | null;
  unitNumber?: string | null;
  lastSeenThroughGateway: string;
}

export interface CreateGatewayRequest {
  name: string;
  description?: string;
  gatewayEui: string;
  rfRegion?: string;
  latitude?: number;
  longitude?: number;
}

export interface UpdateGatewayRequest {
  name?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
  // See UpdateDeviceRequest.clearLocation — same semantics here.
  clearLocation?: boolean;
}

export interface GatewayCertificate {
  certificateId: string;
  certificateArn: string;
  certificatePem: string;
  privateKey: string;
  publicKey: string;
}

// Audit log types
export interface AuditLog {
  id: number;
  userId?: string;
  userName?: string;
  userEmail?: string;
  action: string;
  resourceType: string;
  resourceId: string;
  // Backend-resolved display label for users / properties / devices.
  // Null when the target is a different resource type or has been
  // deleted — UI falls back to a UUID-prefix render.
  resourceLabel?: string | null;
  details: string;
  ipAddress?: string;
  createdAt: string;
}

// Alert types
export enum AlertType {
  PipeEmpty = 'PipeEmpty',
  NoUsage30Days = 'NoUsage30Days',
  MinTemperatureExceeded = 'MinTemperatureExceeded',
  MaxTemperatureExceeded = 'MaxTemperatureExceeded',
  LowSignal = 'LowSignal',
  BurstPipeDetected = 'BurstPipeDetected',
  LeakDetected = 'LeakDetected',
  ReverseFlowDetected = 'ReverseFlowDetected',
  LowBattery = 'LowBattery',
  LowTemperature = 'LowTemperature',
  SensusReadError = 'SensusReadError',
  CoverRemoved = 'CoverRemoved',
}

export enum AlertStatus {
  Active = 'Active',
  Acknowledged = 'Acknowledged',
  Resolved = 'Resolved',
}

export interface Alert {
  id: string;
  deviceId?: string;
  devEui: string;
  serialNumber: string;
  alertType: AlertType;
  status: AlertStatus;
  lastTransmissionAt: string;
  resolvedAt?: string;
  createdAt: string;
}

export interface SystemHealthDevice {
  deviceId: string;
  devEui: string;
  serialNumber: string;
  unitNumber?: string;
  propertyName?: string;
  category: 'LowBattery' | 'WeakSignal' | 'ConnectionIssues';
  detail?: string;
  rssi?: number;
}

export interface ActivityFeedItem {
  type: 'reading' | 'alert_triggered' | 'alert_resolved';
  deviceId: string;
  serialNumber: string;
  unitNumber?: string;
  description: string;
  alertType?: string;
  readingId?: number;
  timestamp: string;
}
